/**
 * The app's processing state: an animated music-wave, a title and one supporting line.
 *
 * ONE COMPONENT, EVERY SCREEN. Create, the Genre Mixer and Music DNA all wait for the
 * same kind of thing, so they wait the same way — same wave, same layout, same spinner,
 * same 5-second floor (lib/processing.ts). Three screens each rolling their own loader
 * is how "the loading animation should be consistent" quietly stops being true.
 *
 * This is GenerationStatus generalised, not a replacement for it: that component was
 * already exactly this shape with the copy hardcoded to song generation, so it now
 * renders this with its own words and every other caller supplies theirs. Nothing about
 * the Create screen's waiting state changed.
 *
 * WHY THE BARS ARE BIGGER HERE THAN IN THE PLAYER
 * -----------------------------------------------
 * `bars` defaults to 18 — the figure GenerationStatus used — and the wave takes the
 * width it is given. On the entry states that fill a panel (`size="lg"`) it is the
 * subject of the screen rather than an ornament beside a caption, so it gets more bars
 * and a centred layout. Same component, same seeded PRNG, same pulse keyframes.
 *
 * SIMULATED, and the honesty here matters more than usual. The bars are NOT amplitude
 * data — there is no audio to analyse, and during generation there is not even a song
 * yet. They come from lib/waveform's seeded PRNG, which is why they are marked
 * decorative. What it states is the one true thing — that work is happening — and how
 * long that has been true is the only thing the 5-second floor affects.
 *
 * NO PROGRESS IS INVENTED, AND THERE IS NOW A `progress` PROP. Those two facts are
 * compatible, and the shape of the prop is what makes them compatible: it is
 * `number | null` and it renders a bar ONLY for a number. A generation job reports
 * `progress` (types/domain.GenerationJob), and the current backend-less build has none to
 * report — so every caller passes nothing, `null` is the default, and no bar appears. The
 * day a server reports genuine progress the value arrives here and the bar turns on with
 * no layout change. A bar creeping to 100% on a timer would still be an invention, which is
 * why nothing in this component moves it.
 */
import type { CSSProperties } from 'react';
import { Loader2 } from 'lucide-react';
import { Waveform } from '../player';
import { ProgressBar } from '../ui';
import { seededHeights } from '../../lib/waveform';
import { c } from '../../styles/theme';

export interface ProcessingWaveProps {
  /** What is happening, in the product's words. e.g. "Creating your song". */
  title: string;
  /** One supporting line under it. */
  detail: string;
  /**
   * `sm` sits inline under a form, as the composer's does. `lg` fills the panel it is
   * standing in for, centred, for the entry states that replace a whole stage.
   */
  size?: 'sm' | 'lg';
  /**
   * Seeds the bar heights. Defaults to a fixed string, which is right for generation —
   * there is no song yet to seed from. Pass something stable per screen so two
   * processing states do not draw the identical shape.
   */
  seed?: string;
  /** Bar count. Defaults to 18 inline, 32 at `lg`. */
  bars?: number;
  /**
   * Real, reported progress 0–100 — from a generation job and from nowhere else.
   *
   * `null` MEANS "NOBODY IS REPORTING PROGRESS", and it is the default because that is the
   * truth in this build: `GenerationJob.progress` exists, the mock leaves it at 0 until the
   * job completes (api/mockGenerations), and interpolating a number between those two points
   * would be reporting elapsed time as work completed. When it is null NO BAR IS RENDERED —
   * an empty bar is itself a claim, that progress is being measured and is currently none.
   *
   * Not `number | undefined`: a caller has to say "no progress" out loud, so passing a job's
   * possibly-absent field through without thinking about it is a type error rather than a
   * silent zero.
   */
  progress?: number | null;
  style?: CSSProperties;
}

export default function ProcessingWave({
  title,
  detail,
  size = 'sm',
  seed = 'Lyrixa-processing',
  bars,
  progress = null,
  style,
}: ProcessingWaveProps) {
  const large = size === 'lg';
  const count = bars ?? (large ? 32 : 18);
  /*
    Clamped, because this number comes from a server. A backend reporting 140 or -3 is a bug
    in the backend, and the honest response is to draw a bar the user can read rather than one
    that overflows its track — the value itself is still whatever was reported.
  */
  const percent = progress === null ? null : Math.min(100, Math.max(0, Math.round(progress)));

  return (
    <div
      // A live region so the wait is announced rather than only drawn: without it a
      // screen reader user gets silence between pressing the button and the toast.
      role="status"
      aria-live="polite"
      className={large ? 'lyx-processing lyx-processing-lg' : 'lyx-processing'}
      style={{
        background: c.surface1,
        border: `1px solid ${c.border}`,
        borderRadius: 12,
        ...style,
      }}
    >
      {large ? (
        <>
          {/* The wave first at this size, because it IS the screen — the words are the
              caption to it rather than the other way round. */}
          <div className="lyx-processing-wave-lg" aria-hidden="true">
            <Waveform heights={seededHeights(seed, count)} playing fill />
          </div>
          <p
            style={{
              fontSize: 14,
              fontWeight: 500,
              margin: '16px 0 4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <Loader2 size={14} color={c.accent} className="lyx-spin" />
            {title}
          </p>
          <p style={{ fontSize: 12, color: c.text2, margin: 0 }}>{detail}</p>
          {/* Under the caption, per the note this component's header used to promise it
              would be. Absent entirely when nothing is reporting — see `progress`. */}
          {percent !== null && (
            <div style={{ margin: '12px auto 0', maxWidth: 260 }}>
              <ProgressBar value={percent} max={100} height={4} label={`${title} — ${percent}%`} />
            </div>
          )}
        </>
      ) : (
        <>
          <Loader2 size={16} color={c.accent} className="lyx-spin" style={{ flexShrink: 0 }} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ fontSize: 13, fontWeight: 500, margin: '0 0 2px' }}>{title}</p>
            <p style={{ fontSize: 11, color: c.text2, margin: 0 }}>{detail}</p>
            {percent !== null && (
              <div style={{ marginTop: 6 }}>
                <ProgressBar
                  value={percent}
                  max={100}
                  height={3}
                  label={`${title} — ${percent}%`}
                />
              </div>
            )}
          </div>
          {/* Hidden on a phone, where there is no width for it beside the caption and
              the spinner already says the same thing. See .lyx-generation-wave. */}
          <div className="lyx-generation-wave" style={{ flexShrink: 0 }}>
            <Waveform heights={seededHeights(seed, count)} playing />
          </div>
        </>
      )}
    </div>
  );
}
